import { failLoud } from '@resource-kit/core';
import type { Modifier, ModifierCtx } from '@resource-kit/core';
import type { AnyFieldDef, ModelField, ViewItem } from '@resource-kit/model';

/**
 * Modifier evaluation (#1799).
 *
 * It lives HERE rather than in `@resource-kit/core` because it is typed against
 * `ViewItem` / `ModelField` / `AnyFieldDef`, which are the resource MODEL's
 * vocabulary — and core's closed bare set is `['react']`, so it may not name
 * `@resource-kit/model` at all. It is root-half rather than `react/` because a
 * modifier is a predicate over a record, not a rendering decision: nothing
 * below evaluates JSX or touches a hook.
 *
 * A `Modifier<T>` is `boolean | (ctx) => boolean`. The predicate form needs a
 * `ModifierCtx`; surfaces that cannot supply one (a list column's default
 * visibility is a table-level decision — there is no single row behind it)
 * pass `null` and a predicate there is an authoring mistake, reported loudly
 * rather than silently resolved to `false`.
 *
 * MODIFIERS ARE UI, NEVER SECURITY. A `readonly` that reads
 * `ctx.user.permissions` must have a matching backend 403; the field set the
 * frontend allows may only ever be a subset of what the backend allows.
 */
export function resolveModifier<T>(
  modifier: Modifier<T> | undefined,
  ctx: ModifierCtx<T> | null,
): boolean {
  if (modifier === undefined) return false;
  if (typeof modifier === 'boolean') return modifier;
  if (ctx === null) {
    failLoud(
      'a modifier predicate was declared on a surface that supplies no ModifierCtx; ' +
        'use a boolean there, or route the surface through a context-carrying caller',
    );
    return false;
  }
  return modifier(ctx);
}

/**
 * `required` is NOT like the presentation modifiers.
 *
 * `invisible` / `readonly` / `columnInvisible` have no counterpart on the
 * model, so an absent value simply means `false`. `required` DOES have one, so
 * an absent value means "do not override" and the model's own `required` wins.
 * Folding the two together would turn "the model says required, the view says
 * nothing" into "not required" — a silently weakened form.
 */
export function resolveRequired<T>(
  modifier: Modifier<T> | undefined,
  ctx: ModifierCtx<T> | null,
  modelRequired: boolean | undefined,
): boolean {
  if (modifier === undefined) return modelRequired === true;
  return resolveModifier(modifier, ctx);
}

export interface ResolvedModifiers {
  invisible: boolean;
  readonly: boolean;
  columnInvisible: boolean;
  detailInvisible: boolean;
  required: boolean;
}

/**
 * Resolve one view item against its model field. The single place where the
 * two `undefined` semantics above are applied, so they cannot drift apart.
 */
export function resolveViewModifiers<T, F extends Record<string, AnyFieldDef>>(
  item: ViewItem<T, F>,
  modelField: ModelField,
  ctx: ModifierCtx<T> | null,
): ResolvedModifiers {
  return {
    invisible: resolveModifier(item.invisible, ctx),
    readonly: resolveModifier(item.readonly, ctx),
    columnInvisible: resolveModifier(item.columnInvisible, ctx),
    detailInvisible: resolveModifier(item.detailInvisible, ctx),
    required: resolveRequired(item.required, ctx, modelField.required),
  };
}
